import { useCallback, useEffect, useRef, useState } from 'react';
import { createListProjectionRuntime } from 'flare-chat-core';
import { initialProjectListWindow, mergeProjectListRecords, normalizeProjectListWindow } from 'flare-chat-core/contracts/project-list-window';

const INITIAL_PROJECTION = Object.freeze({ items: [], error: null, loading: true,
  loadingMore: false, updatedAt: null, window: initialProjectListWindow });
const activeProjectRecords = projects => (Array.isArray(projects) ? projects : [])
  .filter(project => String(project?.status ?? 'active').trim() !== 'archived');

// Track public command completion only. FLARE still owns requests, cache and cancellation.
const trackRead = (owner, command) => {
  owner.reads.add(command);
  const settled = () => owner.reads.delete(command);
  void command.then(settled, settled);
  return command;
};

/** Instance reader/wiring only; FLARE owns cache, request lifecycle and projection state. */
export function useProjectListController({ projectAPI, scopeKey = 'projects', enabled = true } = {}) {
  const ownerRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const [accepted, setAccepted] = useState(null);
  useEffect(() => {
    const read = async ({ append, current, query, signal }) => {
      const response = await projectAPI.listProjects({
        cursor: append ? current.window.nextCursor : '', query, status: 'active',
      }, { signal });
      const incoming = activeProjectRecords(response?.projects);
      return { items: append ? mergeProjectListRecords(current.items, incoming) : incoming,
        window: normalizeProjectListWindow(response?.window) };
    };
    const runtime = createListProjectionRuntime({
      readers: typeof projectAPI?.listProjects === 'function' ? { projects: read } : {},
    });
    const owner = { projectAPI, scopeKey, runtime, reads: new Set() };
    ownerRef.current = owner;
    const publish = () => setAccepted({ ...owner, projection: runtime.getSnapshot().projects });
    const unsubscribe = runtime.subscribe(publish);
    publish();
    void trackRead(owner, runtime.bindScope({ key: scopeKey, enabled: enabledRef.current })).catch(() => {});
    return () => {
      unsubscribe();
      runtime.dispose();
      if (ownerRef.current === owner) ownerRef.current = null;
    };
  }, [projectAPI, scopeKey]);
  useEffect(() => {
    const owner = ownerRef.current;
    if (owner) void trackRead(owner, owner.runtime.bindScope({ key: scopeKey, enabled })).catch(() => {});
  }, [enabled, projectAPI, scopeKey]);

  const loadProjectList = useCallback(async (options = {}) => {
    const owner = ownerRef.current;
    if (!owner || owner.projectAPI !== projectAPI || owner.scopeKey !== scopeKey) return [];
    const { runtime } = owner;
    // Translate existing caller options into public runtime commands, not local decisions.
    if (options.query !== undefined) await trackRead(owner, runtime.search('projects', String(options.query ?? '').trim()));
    else if (options.append === true) await trackRead(owner, runtime.loadMore('projects'));
    else await trackRead(owner, runtime.refresh({ resources: ['projects'], force: options.force !== false }));
    return runtime.getSnapshot().projects.items;
  }, [projectAPI, scopeKey]);
  const invalidateProjectList = useCallback(async () => {
    const owner = ownerRef.current;
    if (!owner || owner.projectAPI !== projectAPI || owner.scopeKey !== scopeKey) return [];
    // A commit invalidates reads started before it, including append reads. Drain
    // their native command acknowledgments before requesting the post-commit view.
    await Promise.allSettled([...owner.reads]);
    if (ownerRef.current !== owner || !enabledRef.current) return [];
    await trackRead(owner, owner.runtime.invalidate('projects'));
    return owner.runtime.getSnapshot().projects.items;
  }, [projectAPI, scopeKey]);
  const loadMoreProjects = useCallback(() => loadProjectList({ append: true }), [loadProjectList]);
  const searchProjects = useCallback(query => loadProjectList({ query }), [loadProjectList]);
  const updateItems = useCallback(nextValue => {
    const owner = ownerRef.current;
    if (owner?.projectAPI === projectAPI && owner.scopeKey === scopeKey) {
      owner.runtime.reconcile('projects', nextValue);
    }
  }, [projectAPI, scopeKey]);
  const projection = accepted?.projectAPI === projectAPI && accepted.scopeKey === scopeKey
    ? accepted.projection : INITIAL_PROJECTION;
  // An accepted empty result is loaded too; runtime's acceptance timestamp is authoritative.
  const loading = projection.loading && projection.updatedAt === null;
  return { ...projection, loading, loadProjectList, invalidateProjectList,
    loadMoreProjects, searchProjects, setItems: updateItems };
}

export default useProjectListController;
